// src/contexts/AuthConfigContext.jsx
import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import api from '@/core/api/api';

const AuthConfigContext = createContext(null);

export const useAuthConfig = () => {
  const context = useContext(AuthConfigContext);
  if (!context) {
    throw new Error('useAuthConfig must be used within an AuthConfigProvider');
  }
  return context;
};

export const AuthConfigProvider = ({ children }) => {
  const [config, setConfig] = useState({
    mfa_enabled: true, // Default to secure settings
    email_verification_required: true,
    password_reset_enabled: true,
    change_password_enabled: true,
    mfa_code_length: 6,
    mfa_code_expiry_minutes: 5,
    password_reset_expiry_hours: 2,
    max_login_attempts: 5,
    lockout_duration_minutes: 15,
    environment: 'production',
    enable_email_code_login: true,
    enable_password_login: true
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('🔧 [AuthConfig] Fetching auth configuration from backend...');
        const response = await api.get('/auth/get_config');

        if (response.data.success && response.data.config) {
          const incoming = response.data.config;
          setConfig((prev) => ({
            ...prev,
            ...incoming,
            enable_email_code_login:
              typeof incoming.enable_email_code_login === 'boolean'
                ? incoming.enable_email_code_login
                : prev.enable_email_code_login,
            enable_password_login:
              typeof incoming.enable_password_login === 'boolean'
                ? incoming.enable_password_login
                : prev.enable_password_login
          }));
          console.log('🔧 [AuthConfig] Auth config loaded successfully:', response.data.config);
        } else {
          throw new Error('Invalid config response format');
        }

      } catch (err) {
        console.error('🔧 [AuthConfig] Failed to fetch auth config from backend:', err);
        setError(err.message || 'Failed to load authentication configuration');
        
        // Keep default secure settings on error
        console.log('🔧 [AuthConfig] Using default secure auth configuration');
        
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  // Helper functions for common checks - memoized to prevent recreation
  const isMfaEnabled = useMemo(() => () => config.mfa_enabled, [config.mfa_enabled]);
  const isEmailVerificationRequired = useMemo(() => () => config.email_verification_required, [config.email_verification_required]);
  const isPasswordResetEnabled = useMemo(() => () => config.password_reset_enabled, [config.password_reset_enabled]);
  const isChangePasswordEnabled = useMemo(() => () => config.change_password_enabled, [config.change_password_enabled]);
  const isDevelopment = useMemo(() => () => config.environment === 'development', [config.environment]);
  const isProduction = useMemo(() => () => config.environment === 'production', [config.environment]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    config,
    loading,
    error,
    
    // Convenience methods
    isMfaEnabled,
    isEmailVerificationRequired,
    isPasswordResetEnabled,
    isChangePasswordEnabled,
    isDevelopment,
    isProduction,
    
    // Direct config access
    mfaEnabled: config.mfa_enabled,
    emailVerificationRequired: config.email_verification_required,
    passwordResetEnabled: config.password_reset_enabled,
    changePasswordEnabled: config.change_password_enabled,
    mfaCodeLength: config.mfa_code_length,
    mfaCodeExpiryMinutes: config.mfa_code_expiry_minutes,
    passwordResetExpiryHours: config.password_reset_expiry_hours,
    maxLoginAttempts: config.max_login_attempts,
    lockoutDurationMinutes: config.lockout_duration_minutes,
    environment: config.environment,
    enableEmailCodeLogin: config.enable_email_code_login !== false,
    enablePasswordLogin: config.enable_password_login !== false
  }), [
    config,
    loading,
    error,
    isMfaEnabled,
    isEmailVerificationRequired,
    isPasswordResetEnabled,
    isChangePasswordEnabled,
    isDevelopment,
    isProduction
  ]);

  return (
    <AuthConfigContext.Provider value={contextValue}>
      {children}
    </AuthConfigContext.Provider>
  );
};

AuthConfigProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default AuthConfigProvider;
